import { Avatar, AvatarFallback } from '@apps/web/components/ui/avatar';
import { cn } from '@apps/web/lib/utils';

const AVATAR_TONES = [
    'bg-sky-100 text-sky-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-violet-100 text-violet-700',
    'bg-rose-100 text-rose-700',
    'bg-cyan-100 text-cyan-700',
] as const;

// Saturated counterpart to AVATAR_TONES, for avatars that carry identity on
// their own rather than sitting beside a name. Same hue order, so a person
// keeps a recognisable colour across both treatments.
const AVATAR_SOLID_TONES = [
    'bg-sky-600 text-white',
    'bg-emerald-600 text-white',
    'bg-amber-600 text-white',
    'bg-violet-600 text-white',
    'bg-rose-600 text-white',
    'bg-cyan-600 text-white',
] as const;

// Brand fills from docs/design/brand/tokens.css. Soft stops so a row of
// conversation avatars does not turn the inbox into a wall of signature purple.
const CONVERSATION_AVATAR_TONES = [
    'bg-[#EEEAFD] text-[#4629C7]',
    'bg-[#DDD5FA] text-[#5533EE]',
    'bg-[#C2EBF5] text-[#002429]',
    'bg-[#DCEAE4] text-[#1E4B40]',
    'bg-[#FDE5DE] text-[#002429]',
    'bg-[#F6F4FE] text-[#5533EE]',
] as const;

export function getAvatarInitials(displayName: string): string {
    const parts = displayName
        .trim()
        .split(/\s+/)
        .filter((part) => part.length > 0);
    if (parts.length === 0) {
        return '?';
    }
    const firstInitial = parts[0]?.[0] ?? '';
    const lastInitial =
        parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
}

// Stable color: hash the key (prefer a stable id like a user id over a name, so
// the tone does not shift when a display name changes).
export function getAvatarToneClass(key: string): string {
    return (
        AVATAR_TONES[hashAvatarKey(key) % AVATAR_TONES.length] ??
        AVATAR_TONES[0]
    );
}

export function getAvatarSolidToneClass(key: string): string {
    return (
        AVATAR_SOLID_TONES[hashAvatarKey(key) % AVATAR_SOLID_TONES.length] ??
        AVATAR_SOLID_TONES[0]
    );
}

export function getConversationAvatarToneClass(key: string): string {
    return (
        CONVERSATION_AVATAR_TONES[
            hashAvatarKey(key) % CONVERSATION_AVATAR_TONES.length
        ] ?? CONVERSATION_AVATAR_TONES[0]
    );
}

function hashAvatarKey(key: string): number {
    return [...key].reduce(
        (total, character) => total + character.charCodeAt(0),
        0,
    );
}

type UserAvatarProps = {
    readonly displayName: string;
    readonly colorKey?: string;
    readonly className?: string;
    readonly toneClassName?: string;
};

export function UserAvatar({
    displayName,
    colorKey,
    className,
    toneClassName,
}: UserAvatarProps) {
    return (
        <Avatar className={cn('size-5', className)}>
            <AvatarFallback
                className={cn(
                    'text-[10px] font-medium',
                    toneClassName ??
                        getAvatarToneClass(colorKey ?? displayName),
                )}
            >
                {getAvatarInitials(displayName)}
            </AvatarFallback>
        </Avatar>
    );
}
